import { FC, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import Image from 'next/image';
import { clsx, useStyles } from '@sweatpals/react-ui-kit';
import { aboutPageStyles } from './about-page.styles';
import { HeaderV2 } from '@/modules/_core/components/header/header-v2.component';
import GetNewsLetterSectionV2 from '../../../landing/components/get-newsletter/get-newsletter-section-v2.component';
import FooterV2 from '@/modules/_core/components/footer/footer-v2.component';
import HeroScreen from '../../../event/components/hero-screen/hero-screen.component';
import ImageWrapper from '@/modules/_core/components/images/Image-wrapper';
import aboutUsHeroImage from '@/public/assets/images/about-us-hero.webp';
import aboutText1 from '@/public/assets/images/about-text-1.webp';
import aboutText2 from '@/public/assets/images/about-text-2.webp';
import promoteCommunityImage from '@/public/assets/images/promote-community-image.webp';
import ExternalLink from '@/modules/_core/components/links/External-link';
import { CONTACT_US_SEND_EMAIL } from '@/modules/_core/constants';
import Masonry from '@mui/lab/Masonry';
import AppButton from "@/modules/_core/components/buttons/app-button.component";
import { Testimonials } from "@/modules/_core/constants/testimonials";
import { TestimonialCard } from "../../components/testimonial-card/testimonial-card.component";
import { useQuery } from "@tanstack/react-query";
import { eventsService, usersService } from "@/modules/_core/services";
import { addDays } from "date-fns";
import { SentryFeedbackButton } from '@/modules/_core/components/buttons/SentryFeedbackButton';

const AboutPage: FC = () => {
	const [showMoreTestimonials, setShowMoreTestimonials] = useState(false);
	const [playingVideoIdx, setPlayingVideoIdx] = useState<number>();
	const styles = useStyles(aboutPageStyles, showMoreTestimonials);
	const { data: eventsCount } = useQuery(['eventsCount'], async () => eventsService.getEventsCountPublic({
		periodFrom: new Date(),
		periodTo: addDays(new Date(), 365),
	}));
	const { data: communities } = useQuery(['communities'], async () => usersService.findAllPublic({
		isCommunityProfile: true,
		pageSize: 0,
	}));

	return (
		<Box sx={styles.root}>
			<HeaderV2 />

			<HeroScreen
				rootSx={{
					minHeight: '50vh',
				}}
				backgroundSx={{
					background: 'grey',
				}}
				foregroundSx={{
					gap: '40px',
					textAlign: { xs: 'center' },
				}}
				background={<img src={aboutUsHeroImage.src} alt="about us hero image" style={styles.heroBackground} />}
			>
				<Typography sx={styles.heroText}>
					Bringing the world together <br /> through fitness
				</Typography>
			</HeroScreen>

			<Box sx={styles.aboutTitleWrapper}>
				<Typography sx={styles.aboutSmallerText}>about us</Typography>
				<Typography sx={styles.aboutTitle}>The SweatPals story</Typography>
			</Box>

			<Box sx={styles.aboutTextWrapper}>
				<Box sx={styles.aboutTextRow}>
					<ImageWrapper>
						<Image src={aboutText1} alt="about text 1" />
					</ImageWrapper>
					<Box sx={clsx(styles.flexColumn, styles.gap('32px'))}>
						<Typography>
							In a world where genuine connections are often missed behind screens, SweatPals is changing the game.
						</Typography>
						<Typography>
							Born from the personal journey of our founder, Salar, who struggled to connect in a new country due to
							language and cultural barriers, until he joined his first run club.{' '}
						</Typography>
						<Typography sx={styles.aboutBoldText}>
							Fitness had this incredible power to bring people together, transcending boundaries of origin, religion,
							or accent. SweatPals is all about making fitness a universal language of unity.{' '}
						</Typography>
					</Box>
				</Box>
				<Box sx={styles.aboutTextRow}>
					<Box sx={clsx(styles.flexColumn, styles.gap('32px'))}>
						<Typography>
							With over 700,000 vibrant fitness communities in the US alone, there&apos;s a strong demand for shared
							experiences beyond the bar scene.
						</Typography>
						<Typography>
							On the other side of the coin, community leaders face the daunting task of juggling various roles while
							managing their communities. SweatPals is the solution they&apos;ve been waiting for, providing all-in-one
							tools to simplify community building, management, and monetization.
						</Typography>
						<Typography sx={styles.aboutBoldText}>
							At SweatPals, we&apos;re here to bridge the gap between fitness enthusiasts and the communities they
							crave, all while empowering community leaders to focus on what they love. Welcome to SweatPals, where real
							connections happen through sweat, not screens!
						</Typography>
					</Box>
					<ImageWrapper>
						<Image src={aboutText2} alt="about text 2" />
					</ImageWrapper>
				</Box>
			</Box>

			<Box sx={styles.valuesWrapper}>
				<Typography sx={styles.valuesTitle}>
					Our values are at core
					<br />
					of everything we do
				</Typography>
				<Box sx={clsx(styles.flexRow, styles.contentEnd, styles.gap('50px'))}>
					<Box sx={styles.valuesItem}>
						<Box sx={styles.valuesItemHeaderWrapper}>
							<Box sx={styles.valueItemTitle1}>01</Box>
							<Box sx={styles.valueItemTitle2}>Kindness</Box>
						</Box>
						<Typography sx={styles.valueItemText}>
							We are committed to fostering <br /> a world where kindness leads in every <br /> interaction and
							decision.
						</Typography>
					</Box>
					<Box sx={styles.valuesItem}>
						<Box sx={styles.valuesItemHeaderWrapper}>
							<Box sx={styles.valueItemTitle1}>02</Box>
							<Box sx={styles.valueItemTitle2}>Diversity</Box>
						</Box>
						<Typography sx={styles.valueItemText}>
							A symphony of voices and talents, <br /> we thrive by uplifting each unique <br /> note of brilliance.
						</Typography>
					</Box>
					<Box sx={styles.valuesItem}>
						<Box sx={styles.valuesItemHeaderWrapper}>
							<Box sx={styles.valueItemTitle1}>03</Box>
							<Box sx={styles.valueItemTitle2}>Hard Work</Box>
						</Box>
						<Typography sx={styles.valueItemText}>
							Fueling dreams with unwavering <br /> dedication-commitment drives <br /> our every move.
						</Typography>
					</Box>
				</Box>
			</Box>

			<Box sx={styles.statsWrapper}>
				<Box sx={styles.statsContent}>
					<Box sx={styles.statsLeftSide}>
						<Box sx={styles.statsItem}>
							<Box sx={styles.statsItemNumber}>{communities?.total}</Box>
							<Box sx={styles.statsItemTitle}>communities</Box>
						</Box>
						<Box sx={styles.statsItem}>
							<Box sx={styles.statsItemNumber}>4</Box>
							<Box sx={styles.statsItemTitle}>cities launched</Box>
						</Box>
						<Box sx={styles.statsItem}>
							<Box sx={styles.statsItemNumber}>48</Box>
							<Box sx={styles.statsItemTitle}>ambassadors</Box>
						</Box>
						<Box sx={styles.statsItem}>
							<Box sx={styles.statsItemNumber}>{eventsCount ?? 0}</Box>
							<Box sx={styles.statsItemTitle}>events created</Box>
						</Box>
					</Box>
					<Box sx={styles.statsRightSide}></Box>
				</Box>
			</Box>

			<Box sx={clsx(styles.flexColumn, styles.gap('50px'), styles.px('60px'), styles.pb('100px'))}>
				<Typography
					sx={{
						fontFamily: 'Integral CF',
						fontWeight: '500',
						fontSize: '40px',
						fontStyle: 'italic',
					}}
				>
					Wall of love
				</Typography>
				<Box sx={styles.testimonialsWrapper}>
					<Masonry columns={4} spacing={3}>
						{Testimonials.map((testimonial, idx) => (
							<TestimonialCard
								key={idx}
								testimonial={testimonial}
								isVideoPlaying={playingVideoIdx === idx}
								onVideoClick={() => { setPlayingVideoIdx(playingVideoIdx === idx ? undefined : idx); }}
							/>
						))}
					</Masonry>
					<Box sx={styles.testimonialsBottomShadow} />
				</Box>
				<Box display="flex" justifyContent="center">
					<AppButton color="secondary" onClick={() => { setShowMoreTestimonials(x => !x); }}>
						Show {showMoreTestimonials ? 'less' : 'more'}
					</AppButton>
				</Box>
			</Box>

			<Box
				sx={clsx(styles.px('60px'), styles.flexRow, styles.pt('130px'), styles.pb('180px'), {
					backgroundColor: '#120E0D',
					color: 'white',
				})}
			>
				<Box>
					<ImageWrapper>
						<Image src={promoteCommunityImage} alt="promote community" />
					</ImageWrapper>
				</Box>
				<Box sx={clsx(styles.flexColumn, styles.growInvestWrapper)}>
					<Typography sx={styles.leadTheChargeText}>
						Lead the charge <br/>
						in transforming <br/>
						fitness <br/>
						communities
					</Typography>
					<ExternalLink href="/create/community">
						<AppButton sx={styles.growCommunityButton}>Grow my community on SweatPals</AppButton>
					</ExternalLink>
					<ExternalLink href={`mailto:${CONTACT_US_SEND_EMAIL}`}>
						<AppButton sx={styles.investInSweatPalsButton}>Invest in SweatPals</AppButton>
					</ExternalLink>
				</Box>
			</Box>

			<GetNewsLetterSectionV2 />

			<FooterV2 />

			<SentryFeedbackButton />
		</Box>
	);
};

export default AboutPage;
